<script>
    import WidgetBox from './WidgetBox.svelte';
    import { tick } from 'svelte';
    import { slide } from 'svelte/transition';
    import { pin } from './pincelate-store.js';
    import ExampleButton from './util/ExampleButton.svelte';
    import CopyToClipboard from './util/CopyToClipboard.svelte';

	let words = [];
    let combined = '';
    let decodingInProgress = false;
    let widgetbox;

    let frame = async() => new Promise(resolve => { requestAnimationFrame(resolve); });

    function reset() {
        words = ['ketchup', 'mustard'];
        combined = '';
    }

    reset();

    async function mixWrap() {
        decodingInProgress = true;
        widgetbox.startDecode();
        await tick();
        setTimeout(async function() {
            await mix();
            decodingInProgress = false;
            widgetbox.endDecode();
            await tick();
        }, 100);
    }

    async function mix() {
        await frame();
        combined = "";
        let states;
        for await (let result of $pin.phonemeStateAsync(words)) {
            combined += "▒"; // I can't believe no one told me to change this
            await frame();
            if (result[0] == 'orth_state') {
                states = result[1];
                break;
            }
        }

        await frame();

        combined = "";
        for await (let timestep of $pin.spellStateAsync(states.sum(0).div(states.shape[0]).expandDims(), 0.35)) {
            combined += timestep[0];
            await frame();
        }

    }

    let trickData = [
        "Try mixing the names of your family, friends, pets, celebrities, favorite foods, etc.",
        "You can add up to six words to the mixer!",
        "Need inspiration? Look around the room. Mix the names of the first two things you see.",
        "The model only works with lowercase letters. Sorry!",
        "Mixing involves a bit of chance. If you don't like the first result, tap 'Mix' again."
    ];

    $: {
        for (let i = 0; i < words.length; i++) {
            words[i] = words[i].replaceAll(/[^A-Za-z'\-]/g, "").toLowerCase();
        }
    }

</script>

<style>

    .field-and-button {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    .add-button {
        background-image: url("assets/svg/_plus-medium-blue.svg");
    	background-size: 100%;
	    background-origin: border-box;
        width: 1.25rem;
        height: 1.25rem;
        border-radius: 0.75rem;
        border: 0.15rem white solid;
    }

    .remove-button {
        position: relative;
        background-image: url("assets/svg/_x-small-blue.svg");
    	background-size: 100%;
	    background-origin: border-box;
        width: 1.25rem;
        height: 1.25rem;
        border-radius: 0.75rem;
        border: 0.15rem white solid;
        margin: 0.5rem;
        cursor: pointer;
    }

    .remove-button:after { /* make the clickable area bigger */
        content: "";
        position: absolute;
        width: 2em;
        height: 2em;
        left: -0.5em;
        top: -0.5em;
    }

</style>

<WidgetBox on:reset={reset} {trickData} bind:this={widgetbox}>

    <span slot="title">
        Mixer
    </span>

    <span slot="tagline">
        Mix together existing words to make new meanings
    </span>

    <div slot="widget" id="wordmixer">

        <div class="box dark lev1" style="position: relative; z-index: 1;" transition:slide|local>

            {#each words as word, i}
            <div class="field-and-button" transition:slide|local="{{delay: 150}}">
                <input bind:value={word} type="text" maxlength="16" style="width: 85%;" class="box medium light lev2 light-border">
                {#if (i > 1)}
                <button class="remove-button hovery" title="Remove" on:click={() => {words.splice(i, 1); words = words; }}>
                    &nbsp;
                </button>
                {/if}
            </div>
            {/each}

        </div>

        <div class="control-container" transition:slide|local>
            <div class="control-tab secondary-action box light lev1">
                <button class="add-button hovery"
                    id="add"
                    disabled={words.length >= 6}
                    on:click={() => { words = [...words, ""] }}
                    title="Add word">&nbsp;</button>
                <label for="add">WORD</label>
            </div>
            <div class="control-tab primary-action box light lev1">
                <button id="mix" class="action-button blue hovery" title="Mix"
                class:processing={decodingInProgress} disabled={decodingInProgress} on:click={mixWrap}>&nbsp;</button>
                <label for="mix">MIX</label>
            </div>
        </div>

        <div class="box dark lev1" class:jittering={decodingInProgress} transition:slide|local="{{delay: 150}}">
            <div class="box blue lev2 light-border">
                <div class="box medium light lev3 light-border" style="display: flex; justify-content: space-between;">
                    {#if combined.length > 0}
                        <div>{combined}</div> {#if !decodingInProgress}<CopyToClipboard text={combined}/>{/if}
                    {:else}
                        &nbsp;
                    {/if}
                </div>
            </div>
        </div>

    </div>

    <div slot="modalContent">

        <p>The <b>Mixer</b> spells out a new nonsense word that combines the sounds of the
        words you type in.</p>

        <h4>How to use it</h4>
        <ul>
            <li>Put the words you want to mix together into the text fields.</li>
            <li>Tap <ExampleButton src="assets/svg/_plus-medium-blue.svg" alt="Add">Word</ExampleButton> to add a
            new word. (You can add up to six words, but the tool works best with just two or
            three.)</li>
            <li>Then, tap <ExampleButton src="assets/svg/_action-large-blue.svg" alt="Action">Mix</ExampleButton>
            to start mixing! The resulting word will appear after a moment in the output box at the bottom of the interface.</li>
            <li>Too many words? You can remove unwanted fields with
            <ExampleButton src="assets/svg/_x-small-blue.svg" alt="Remove"></ExampleButton>.</li>
        </ul>

        <h4>Things to try</h4>

        <ul>
            <li>Look around the room you're in. If you combined those two objects together,
                what would the new object be called? The <b>Mixer</b> can help you find out.</li>
            <li>Try mixing the names of your family, friends, pets, celebrities, favorite foods,
                etc.</li>
        </ul>
            
        
    </div>

</WidgetBox>